import React, { useEffect, useState, useMemo } from 'react';

import { Alert, FormGroup, FormControl, ControlLabel } from 'react-bootstrap';

import * as Service from '../../services/ActivitiesService';
import { ExploreConfig } from '../../types/City';
import ActivityFormMultiSelector from '../activities/ActivityForm/ActivityFormMultiSelector/ActivityFormMultiSelector';
import Error from '../stateMessage/Error';
import RadioButtons from '../utils/FormWidgets/RadioButtons';

const CITY_EXPLORE: ExploreConfig = {
  title: '',
  subtitle: '',
  tags: [],
  isRandomized: false,
  isGlobal: false,
};

function filterTags(tags) {
  return tags?.filter(
    tag =>
      tag.name !== 'hidden' &&
      tag.type !== 'hidden' &&
      tag.type !== 'additional booking info'
  );
}

function getTags() {
  return Service.getAllTags();
}

type Props = {
  cityExplore?: ExploreConfig;
  isOOMConfig?: boolean;
  onSubmit: (config: ExploreConfig, addMore: boolean, receiver: string) => void;
};

export const ExploreConfigurationForm = ({
  cityExplore,
  isOOMConfig = false,
  onSubmit,
}: Props) => {
  const [explore, setExplore] = useState(
    cityExplore || { ...CITY_EXPLORE, isGlobal: isOOMConfig }
  );
  const [isNew, setIsNew] = useState(true);
  const [allValidTags, setAllValidTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(
    cityExplore ? filterTags([...cityExplore.tags]) : []
  );
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    let exploreTags = [];
    async function getValidTags() {
      try {
        const allTags = await getTags();
        return filterTags(allTags);
      } catch (e) {
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        throw new Error(e);
      }
    }

    getValidTags()
      .then(data => {
        exploreTags = data;
        setAllValidTags(exploreTags);
      })
      .catch(e => {
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        throw new Error(e);
      });

    if (cityExplore) {
      setExplore(cityExplore);
      setIsNew(false);
    } else {
      setIsNew(true);
    }
  }, []);

  const onTitleChange = ({ target }) => {
    setExplore({ ...explore, title: target.value });
  };

  const onSubtitleChange = ({ target }) => {
    setExplore({ ...explore, subtitle: target.value });
  };

  const onTagChange = tag => {
    let newTags = [];
    if (selectedTags.includes(tag)) {
      newTags = selectedTags.filter(t => t !== tag);
    } else {
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      newTags = [...selectedTags, tag];
    }

    setSelectedTags(newTags);
    setExplore({ ...explore, tags: selectedTags });
  };

  const onIsRandomizedChange = () => {
    setExplore({ ...explore, isRandomized: !explore.isRandomized });
  };

  const onIsGlobalChange = () => {
    setExplore({ ...explore, isGlobal: !explore.isGlobal });
  };

  const validate = () =>
    explore.title.trim().length > 0 &&
    explore.subtitle.trim().length > 0 &&
    selectedTags.length > 0;

  const submitElement = addMore => {
    if (selectedTags.length > 1) {
      setShowAlert(true);
      return;
    }
    const newExplore = {
      ...explore,
      title: explore.title.trim(),
      subtitle: explore.subtitle.trim(),
      tags: selectedTags,
    };

    if (addMore) {
      setExplore(CITY_EXPLORE);
      setSelectedTags([]);
    }
    onSubmit(newExplore, addMore, 'exploreConfigurations');
  };

  const notSelectedTags = useMemo(
    () =>
      allValidTags
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        .filter(tag => !selectedTags.includes(tag.name))
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        .map(tag => tag.name),
    [selectedTags, allValidTags]
  );

  const text = isNew ? 'Create' : 'Edit';
  const buttonText = isNew ? 'Create' : 'Update';

  const ValidateTagsAlert = () => (
    <Alert className='alert-danger'>
      <p>
        Only 1 associated tag is allowed per each explore section. Please try
        again.
      </p>
    </Alert>
  );

  return (
    <>
      <h4 className='modal-head'>{`${text} Explore`}</h4>
      <FormGroup style={{ paddingLeft: '0px' }}>
        <ControlLabel>Title</ControlLabel>
        <FormControl onChange={onTitleChange} value={explore.title} />
      </FormGroup>
      <FormGroup style={{ paddingLeft: '0px' }}>
        <ControlLabel>Subtitle</ControlLabel>
        <FormControl onChange={onSubtitleChange} value={explore.subtitle} />
      </FormGroup>
      <FormGroup>
        {showAlert && <ValidateTagsAlert />}
        <ControlLabel>Activity Tags</ControlLabel>
        {
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          <ActivityFormMultiSelector
            label='Tag'
            caption={"Applied Tags"}
            selectedItems={selectedTags}
            options={notSelectedTags}
            onItemClick={onTagChange}
          />
        }
      </FormGroup>
      <FormGroup controlId='Randomize'>
        <ControlLabel>Randomize?</ControlLabel>
        <RadioButtons
          label='randomize'
          name='randomize'
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onRadioChange={() => onIsRandomizedChange()}
          value={explore.isRandomized || false}
        />
      </FormGroup>
      <FormGroup controlId='Global'>
        <ControlLabel>Global?</ControlLabel>
        <RadioButtons
          disabled={isOOMConfig}
          label='global'
          name='global'
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onRadioChange={() => onIsGlobalChange()}
          value={explore.isGlobal}
        />
      </FormGroup>
      <button
        type='button'
        className='btn btn-primary pull-right'
        disabled={!validate()}
        style={{ marginTop: '25px', marginRight: '0px' }}
        onClick={() => submitElement(false)}
        aria-label='left Align'
      >
        {buttonText}
      </button>
      {isNew && (
        <button
          type='button'
          className='btn btn-def pull-right'
          disabled={!validate()}
          style={{ marginTop: '25px', marginRight: '15px' }}
          onClick={() => submitElement(true)}
          aria-label='left Align'
        >
          Create and Add More
        </button>
      )}
    </>
  );
};
